<template>
  <section class="section is-marginless">
    <div class="columns is-centered">
      <div class="column is-half">
        <b-message v-if="error_message" type="is-danger">
          {{ error_message }}
        </b-message>
        <h3 class="title">Sign up for {{ domain }}</h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-message v-if="error_message" type="is-danger">
          {{ error_message }}
        </b-message>
        <h3 class="subtitle">Personal Information</h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Salesforce Account ID *">
          <b-input
            size="is-medium"
            required
            placeholder="i.e., 45681746"
            ref="account_id"
            v-model="company.account_id"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Name *">
          <b-input
            size="is-medium"
            required
            placeholder="First name"
            ref="first_name"
            v-model="company.first_name"
          ></b-input>
        </b-field>
        <b-field>
          <b-input
            size="is-medium"
            placeholder="Last name"
            ref="last_name"
            required
            v-model="company.last_name"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Email address *">
          <b-input
            size="is-medium"
            required
            placeholder="name@email.com"
            ref="email"
            v-model="company.email"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <label class="label">Phone *</label>
        <b-field :type="company.phone ? null : required">
          <PhoneCountryPicker ref="phone_country_picker"></PhoneCountryPicker>
          <b-input
            size="is-medium"
            placeholder="1234567890"
            ref="phone"
            v-model="company.phone"
            expanded
            required
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <h3 class="subtitle">Company Details</h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Parent organization">
          <b-autocomplete
            v-model="companyFilter"
            icon-pack="fas"
            icon="search"
            :size="'is-medium'"
            placeholder="Select organization"
            :open-on-focus = true
            :data="filteredCompanyObj"
            field="name"
            @select="option => (company.organization_id = option.organization_id)"
            :clearable = true>
          </b-autocomplete>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Company name *">
          <b-input
            size="is-medium"
            placeholder=""
            required
            ref="name"
            v-model="company.name"
            v-on:blur="handleCompanyNameUpdated()"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half full-address">
        <b-field label="Full Address *" :type="this.isValidAddress ? null : 'is-danger' ">
          <b-autocomplete
            v-on:input="handleAutoCompleteAddress()"
            v-model="company.address"
            ref="address"
            :data="autoCompletes"
            placeholder="123 Some Street, New York, 11010"
            field="description"
            :loading="isFetching"
            @typing="onAddressChange"
            @select="option => (company.address = option.description)"
            icon-pack="fas"
            icon="search"
            size="is-medium"
            required
          >
            <template slot-scope="props">
              <div class="media">
                <div>
                  {{ props.option.description }}
                </div>
              </div>
            </template>
          </b-autocomplete>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Desired dashboard URL *">
          <b-field :type="company.subdomain ? null : required">
            <p class="control">
              <span class="button is-static is-medium">https://</span>
            </p>
            <b-input
              size="is-medium"
              required
              expanded
              ref="subdomain"
              v-model="company.subdomain"
            ></b-input>
            <p class="control">
              <span class="button is-static is-medium">.{{ domainURL }}</span>
            </p>
          </b-field>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Please enter a password to access to your dashboard *">
          <b-input
            size="is-medium"
            placeholder="Password"
            ref="password"
            required
            v-model="company.password"
            type="password"
            autocomplete="off"
          ></b-input>
        </b-field>
        <b-field>
          <b-input
            size="is-medium"
            required
            placeholder="Confirm Password"
            ref="password_confirmation"
            v-model="password_confirmation"
            type="password"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Generate a Super Admin Password*">
          <b-field>
            <b-input
              size="is-medium"
              ref="super_admin_password"
              placeholder="Super Admin Password"
              required
              expanded
              disabled
              readonly
              v-model="company.super_admin_password"
              autocomplete="off"
            ></b-input>
            <p class="control">
              <b-button class="button is-medium" @click="generatePassword">
                <i class="fas fa-sync-alt"></i>
              </b-button>
            </p>
          </b-field>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field>
            <b-checkbox v-model="company.is_test"
                type="is-success">
                Is this a testing company?
            </b-checkbox>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <h3 class="subtitle is-6">
          By clicking 'Create Company' you are agreeing that you have read and
          agree to our
          <a target="_blank" v-bind:href="getTOSLink()">terms of service</a> and
          <a target="_blank" v-bind:href="getPrivacyPolicyLink()"
            >privacy policy</a
          >
        </h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <a
          class="button is-outlined is-medium is-primary is-fullwidth"
          @click="save"
          :disabled="!isValidate"
          v-bind:class="{ 'is-loading': is_loading }"
          v-bind:dir="is_loading"
          >Create Company</a
        >
      </div>
    </div>
  </section>
</template>

<script>
import CompanyService from "./service";
import LocationService from "../location/service";
import { ToastProgrammatic as Toast } from "buefy";
import PhoneCountryPicker from "@/components/common/PhoneCountryPicker";
import Session from "@/session/service";
import _ from "lodash";
import { v4 as uuid } from 'uuid';

export default {
  name: "CompanySignup",
  components: {
    PhoneCountryPicker
  },

  data() {
    return {
      companyFilter: '',
      error_message: null,
      isValidAddress: true,
      domain: "Cannveya",
      domainURL: "dispatch.weedmaps.com",
      company: {
        account_id: "",
        name: this.$route.query.company_name || "",
        subdomain: "",
        address: "",
        email: "",
        phone: "",
        password: "",
        is_test: false,
        super_admin_password: ""
      },
      is_loading: false,
      password_confirmation: null,
      industries: [
        {
          id: "dispensary",
          name: "Dispensary"
        },
        {
          id: "grower",
          name: "Grower/Processor"
        },
        {
          id: "courier",
          name: "Courier"
        },
        {
          id: "delivery_service",
          name: "Delivery Service"
        },
        {
          id: "taxi",
          name: "Taxi"
        },
        {
          id: "other",
          name: "Other"
        }
      ],
      autoCompletes: [],
      is_organization: null,
      organizations: [],
      isFetching: false
    };
  },
  computed: {
    filteredCompanyObj() {
      return this.organizations && this.organizations.filter(option => option.name.toLowerCase().indexOf(this.companyFilter.toLowerCase()) >= 0)
    },
    isValidate() {
      let is_disable =
        this.company.account_id !== "" &&
        this.company.first_name !== "" &&
        this.company.last_name !== "" &&
        this.company.email !== "" &&
        this.company.phone !== "" &&
        this.company.name !== "" &&
        this.company.subdomain !== "" &&
        this.company.password !== "" &&
        this.company.address !== "" &&
        this.password_confirmation &&
        this.company.super_admin_password !== ""
        this.isValidAddress;
      return is_disable;
    }
  },

  mounted() {
    if (Object.keys(this.$route.params).length !== 0) {
      if (!this.$route.params.id === "liverydesk") {
        this.$router.push("CompanySignup");
      }
    }
  },

  created() {
    this.getOrganizations();
  },

  methods: {
    handleAutoCompleteAddress() {
      this.isValidAddress = false;
      this.autoCompletes.forEach((address) => {
        if(address.description === this.company.address){
          this.isValidAddress = true;
        }
      })
    },
    handleCompanyNameUpdated() {
      this.company.subdomain = JSON.parse(JSON.stringify(this.company.name))
        .toLowerCase()
        .replace(/\W+/g, "");
    },

    getPrivacyPolicyLink() {
      if (this.$route.query.privacy_policy_link) {
        return this.$route.query.privacy_policy_link;
      } else {
        return "https://www.cannveya.com/privacy-policy";
      }
    },

    getTOSLink() {
      if (this.$route.query.terms_link) {
        return this.$route.query.terms_link;
      } else {
        return "https://www.cannveya.com/service-agreement";
      }
    },

    getOrganizations() {
      CompanyService.getOrganization((err, organization) => {
        if (err) {
          alert(err);
          return;
        }

        this.organizations = organization.filter(org => org.organization_id).sort((a, b) => a.name.localeCompare(b.name));
      });
    },

    onAddressChange: _.debounce(async function(value) {
      if (!value) {
        this.autoCompletes = [];
        return;
      }
      this.isFetching = true;
      try {
        const response = await LocationService.getAutoComplete(value);
        this.autoCompletes = response.predictions;
      } catch (err) {
        console.log("error", err);
        this.autoCompletes = [];
      } finally {
        this.isFetching = false;
      }
    }, 300),

    onSelectAddress(value) {
      this.company.address = value.item;
    },

    save() {
      if (!this.company.account_id) {
        this.$refs.account_id.focus();
        return this.throwError("Invalid salesforce account ID");
      }

      if (!this.company.first_name) {
        this.$refs.first_name.focus();
        return this.throwError("Invalid first name");
      }

      if (!this.company.last_name) {
        this.$refs.last_name.focus();
        return this.throwError("Invalid last name");
      }

      if (!this.company.email) {
        this.$refs.email.focus();
        return this.throwError("Invalid email");
      }

      let phone_country = this.$refs.phone_country_picker.selected_country;
      if (!this.company.phone) {
        this.$refs.phone.focus();
        return this.throwError("Invalid company phone");
      }

      if (!this.company.name) {
        this.$refs.name.focus();
        return this.throwError("Invalid company name");
      }

      if (!this.company.address || !this.isValidAddress) {
        this.isValidAddress = false;
        this.$refs.address.focus();
        return this.throwError("Invalid company address");
      }

      if (!this.company.subdomain) {
        this.$refs.subdomain.focus();
        return this.throwError("Invalid dashboard URL");
      }

      if (!this.company.password) {
        this.$refs.password.focus();
        return this.throwError("Invalid password");
      }

      if(!this.company.super_admin_password) {
        this.$refs.password.focus();
        return this.throwError("Invalid Super Admin Password");
      }

      const re = new RegExp(/[0-9a-f]{12}4[0-9a-f]{19}/)
      if (!re.test(this.company.super_admin_password)) {
          return this.throwError("Super Admin Password must be 32 characters and follow a UUID format without hyphens");
      }

      this.is_loading = true;

      this.company.phone_country = phone_country.code;

      if (!this.password_confirmation) {
        this.$refs.password_confirmation.focus();
        return this.throwError(
          "Passwords do not match, please re-enter your password and confirmation"
        );
      }

      if (this.company.password !== this.password_confirmation) {
        this.$refs.password.focus();
        return this.throwError(
          "Passwords do not match, please re-enter your password and confirmation"
        );
      }

      // Append any additional params
      const addtl_params = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "ref"
      ];
      for (let param of addtl_params) {
        if (this.$route.query[param]) {
          this.company[param] = this.$route.query[param];
        }
      }

      this.company.send_customer_email = false;
      this.company.type = "cannabis";

      // Specifically clear the access token for this because we don't need it and it can be set if the user used another web component
      // As it's stored in a cookie and our API client can pick it up
      Session.setAccessToken(null);

      CompanyService.create(this.company, (err, company) => {
        this.is_loading = false;

        if (err) {
          Toast.open({ type: "is-danger", message: err.message });
          return;
        }

        Toast.open({ type: "is-success", message: `Signup successful` });
        this.$router.push({ name: "CompanySignupSuccess" });
      });
    },

    throwError(message) {
      this.is_loading = false;
      Toast.open({ type: "is-danger", message });
    },

    generatePassword() {
      const password = uuid().replaceAll('-', '');
      this.company.super_admin_password = password;
      Toast.open({ type: "is-success", message: 'Please copy and upload this password to 1Password' });
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/company/CompanySignup.vue